/**
 * sp-personaliser-helpers.js
 * ------------------------------------------------------------
 * Shared helpers for ALL personalised products — used by both
 *   1. The free-form Canva-style canvas editor
 *      (snippets/flipping-bangers-free-form.liquid)
 *   2. Template-driven personalisers in `full_width` layout mode
 *      (snippets/flipping-bangers-personaliser.liquid)
 *
 * Specifically provides:
 *   - "Design your mug ↓" CTA scroll-to-editor handler
 *   - DOM relocation of the section's qty/ATC block into the
 *     personaliser's bottom area
 *   - Safety-net timer that re-shows the source elements if
 *     relocation hasn't happened (e.g. fatal JS error)
 *
 * These helpers are layout-mode agnostic — they look up the
 * editor by [data-ff-editor] and the relocation drop-slot by
 * [data-ff-relocate-target], and don't care which kind of
 * personaliser is rendering them.
 *
 * Loaded by sections/sipfull-product.liquid for any product
 * that has a personalisation_template (full_width mode) or a
 * free_form_template metafield set. Inline-mode template
 * products don't load this script — they don't need it.
 *
 * The "ff-" attribute prefix is historical (free-form was the
 * first user) and remains for compatibility with existing
 * Liquid markup. Renaming would touch many files; treat the
 * prefix as just a namespace, not a layout-type marker.
 */
(function () {
  'use strict';

  // ============================================================
  // "Design your mug" CTA — scroll-to-editor handler.
  // The CTA button lives in the product info panel (above the
  // pickers), NOT inside the personaliser's DOM root. Event
  // delegation on document handles clicks from anywhere — keeps
  // the CTA working regardless of when the personaliser script
  // (free-form-editor.js or otherwise) finishes booting.
  // ============================================================
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ff-scroll-to-editor]');
    if (!btn) return;
    var editor = document.querySelector('[data-ff-editor]');
    if (!editor) return;
    e.preventDefault();
    editor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // ============================================================
  // "See on mug" handler — scroll UP to the live 3D preview in
  // the gallery. Inverse direction to scroll-to-editor, used by a
  // button at the bottom of the personaliser to close the
  // design-vs-preview loop without forcing the customer to
  // remember the gallery is up there.
  // ============================================================
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ff-scroll-to-3d]');
    if (!btn) return;
    var galleryEl = document.querySelector('[data-fb-gallery]')
                 || document.querySelector('[data-sp-hero-wrap]')
                 || document.querySelector('.sp-gallery');
    if (!galleryEl) return;
    e.preventDefault();
    galleryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // ============================================================
  // Relocate the buy controls (.sp-qty-atc) from the section's
  // info column into the personaliser's bottom area.
  // ------------------------------------------------------------
  // The theme's <product-form> custom element captures
  //   this.submitBtn = this.querySelector('[name="add"]')
  // and
  //   this.form     = this.querySelector('.js-product-form')
  // in its constructor (assets/main.js line ~2685). It binds the
  // form's submit listener at the same time. Both the references
  // and the listener live on JS objects, so they survive a DOM
  // move of the button — moving the DOM node to a new parent
  // doesn't invalidate `this.submitBtn`. The form's submit event
  // also still bubbles correctly when triggered by the moved
  // submit button (since `<button type="submit">` with form="..."
  // dispatches submit on its associated <form>).
  //
  // We do NOT relocate Shop Pay / Apple Pay (.sp-payment-buttons).
  // Those are Shopify-rendered web components that wire up to the
  // form via lifecycle callbacks (connectedCallback /
  // disconnectedCallback). Re-parenting fires both, and the new
  // parent — outside the form — has no form ancestor to wire to,
  // so clicks stop redirecting to checkout. Hidden via Liquid-
  // stamped [data-ff-shoppay-hidden] for full-width-personaliser
  // products and never moved. Express checkout is still reachable
  // from the cart drawer post-add-to-cart.
  //
  // Liquid stamps [data-ff-relocate-pending] on the qty/ATC source
  // element (only for full-width-personaliser products) and CSS
  // hides anything with that attribute. We remove the attribute
  // after the move, revealing the controls in their new home.
  // ============================================================
  function relocateBuyControls() {
    var target = document.querySelector('[data-ff-relocate-target]');
    if (!target) return; // no full-width personaliser on this page
    var qtyAtc = document.querySelector('[data-sp-qty-atc][data-ff-relocate-pending]');
    if (qtyAtc) {
      target.parentNode.insertBefore(qtyAtc, target);
      qtyAtc.removeAttribute('data-ff-relocate-pending');
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateBuyControls);
  } else {
    relocateBuyControls();
  }

  // Safety net: if relocation hasn't happened within 4 seconds
  // (e.g. a fatal error in another script before relocate ran),
  // unhide the source elements at their original location so the
  // customer can still buy. This is the last-line defence —
  // relocation should always run within ~50ms of DOMContentLoaded
  // under normal conditions.
  setTimeout(function () {
    var stuck = document.querySelectorAll('[data-ff-relocate-pending]');
    if (stuck.length) {
      console.warn('[sp-personaliser-helpers] relocation safety net: revealing %d stranded element(s)', stuck.length);
      stuck.forEach(function (el) { el.removeAttribute('data-ff-relocate-pending'); });
    }
  }, 4000);

  // ============================================================
  // PDP-conv-2026-05: "Design your mug" CTA reverts to ATC once the
  // user has engaged with the editor.
  // ------------------------------------------------------------
  // Initial state: top of the info column shows the "Design your
  // mug ↓" CTA — clicking it scrolls down to the editor.
  // After the first click anywhere inside the editor (i.e. the user
  // has clearly engaged with the design experience), the top CTA
  // swaps to "Add to cart / Handpressed to order" and its click
  // behaviour shifts from scroll-to-editor to triggering the real
  // ATC button (which by now has been DOM-relocated to the bottom
  // of the editor).
  //
  // Why: customers scroll back UP to review the 3D preview after
  // designing. If the top button still says "Design your mug" they
  // can't find the ATC to buy from there. Swapping it to ATC mode
  // gives them a way to buy from the top — and triggering the real
  // ATC keeps all form-wiring (variant id, qty, cart drawer, AJAX)
  // going through the single code path. No second add-to-cart
  // pipeline to maintain.
  //
  // The scroll-to-editor handler at the top of this file is
  // event-delegated on document and checks for the
  // [data-ff-scroll-to-editor] attribute via .closest(). Removing
  // the attribute on the CTA after swap is enough to disable the
  // scroll behaviour — no listener teardown required. Our new
  // click handler is attached directly to the button so it fires
  // first (capture order: target → bubble path), then the document
  // delegation bails because the attribute is gone.
  // ============================================================
  function initEditorCtaSwap() {
    var editor = document.querySelector('[data-ff-editor]');
    if (!editor) return;
    var cta = document.querySelector('[data-ff-cta]');
    if (!cta) return;

    var swapped = false;

    function swapToAtcMode() {
      if (swapped) return;
      swapped = true;

      var main = cta.querySelector('.sp-atc-main');
      var meta = cta.querySelector('.sp-atc-meta');
      if (main) main.textContent = 'Add to cart';
      if (meta) meta.textContent = 'Handpressed to order';

      // Remove the scroll-to-editor attribute so the delegated
      // handler bails. Our direct click handler (added below) takes
      // over with ATC behaviour.
      cta.removeAttribute('data-ff-scroll-to-editor');

      // Add the data-sp-edit-armed marker so any CSS rule that
      // wants to style the swapped state can hook in.
      cta.setAttribute('data-sp-edit-armed', '');
    }

    cta.addEventListener('click', function (e) {
      if (!swapped) return; // pre-swap: scroll-to-editor handler runs
      e.preventDefault();
      e.stopPropagation();
      // Trigger the real ATC (relocated to the editor's bottom area).
      // Submit and cart drawer wiring happens via main.js's product-form
      // — we just dispatch the click.
      var realAtc = document.querySelector('[data-sp-atc]');
      if (realAtc) realAtc.click();
    });

    // First click anywhere inside the editor flips the swap. Using
    // click (not pointerdown / touchstart) so passive scroll gestures
    // don't accidentally trigger the swap on touch devices.
    editor.addEventListener('click', function () {
      swapToAtcMode();
    }, { once: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEditorCtaSwap);
  } else {
    initEditorCtaSwap();
  }
})();
